import {
	addUnit,
	endOf,
	formatDate,
	isSame,
	startOf,
	subtractUnit,
} from "@sembark-travel/datetime-utils"
import { Box, Inline, Stack, Input, Col, Grid, Text } from "../.."
import { useCallback, useState } from "react"
import Icons from "../Icons"
import DateTime from "./DateTime"
import { TDateRangePreset } from "./types"

export default function DateRangeInput({
	from,
	to,
	onChange,
	dateFormat = "DD MMM, YYYY",
}: {
	from?: Date
	to?: Date
	onChange: (range: { from?: Date; to?: Date }) => void
	dateFormat?: string
}) {
	const [hoverDate, setHoverDate] = useState<Date | undefined>(undefined)
	const effectiveHoverDate = to ? undefined : hoverDate
	const handleHoverDateChange = useCallback(
		(date?: Date) => {
			if (!to) {
				setHoverDate(date)
			}
		},
		[to]
	)

	const fromValue = from ? formatDate(from, dateFormat) : ""
	const toValue = to ? formatDate(to, dateFormat) : ""

	return (
		<Stack gap="4">
			<Inline alignItems="center" gap="2" flexWrap="wrap">
				<Input
					type="text"
					value={fromValue}
					readOnly
					placeholder={dateFormat}
					disabled
					style={{ width: "140px" }}
				/>
				<Box>
					<Icons.ArrowLeft rotate="180" />
				</Box>
				<Input
					type="text"
					value={toValue}
					readOnly
					placeholder={dateFormat}
					disabled
					style={{ width: "140px" }}
				/>
			</Inline>
			<Inline gap="4" alignItems="start" flexWrap="wrap">
				<Box
					style={{ minWidth: "280px" }}
					padding="4"
					borderWidth="1"
					borderColor="default"
					borderRadius="md"
				>
					<DateTime
						mode="range"
						rangeValue={{ from, to }}
						onRangeChange={onChange}
						dateFormat={dateFormat}
						value={from}
						hoverDate={effectiveHoverDate}
						onHoverDateChange={handleHoverDateChange}
					/>
				</Box>
				<Box
					style={{ minWidth: "280px" }}
					padding="4"
					borderWidth="1"
					borderColor="default"
					borderRadius="md"
				>
					<DateTime
						mode="range"
						rangeValue={{ from, to }}
						onRangeChange={onChange}
						dateFormat={dateFormat}
						value={
							from && to && !isSame(to, from, "month")
								? to
								: from
									? addUnit(from, 1, "month")
									: addUnit(new Date(), 1, "month")
						}
						hoverDate={effectiveHoverDate}
						onHoverDateChange={handleHoverDateChange}
					/>
				</Box>
			</Inline>
		</Stack>
	)
}

const DEFAULT_DATE_RANGE_PRESETS: Array<TDateRangePreset> = [
	{ count: 3, unit: "month" },
	{ count: 6, unit: "month" },
	{ count: 1, unit: "year" },
]

export function DateRangeInputWithPresets({
	from,
	to,
	onChange,
	dateFormat = "DD MMM, YYYY",
	presets = DEFAULT_DATE_RANGE_PRESETS,
}: {
	from?: Date
	to?: Date
	onChange: (range: { from?: Date; to?: Date }) => void
	dateFormat?: string
	presets?: Array<TDateRangePreset> | boolean
}) {
	const handlePresetClick = (preset: TDateRangePreset) => {
		const [presetFrom, presetTo] = getRangeForPreset(preset)
		onChange({ from: presetFrom, to: presetTo })
	}

	const gettingPresets = Array.isArray(presets) && presets.length > 0

	return (
		<Grid noGutters>
			{gettingPresets ? (
				<Col xs={12} sm={2}>
					<Stack overflowY="auto" style={{ maxHeight: "320px" }}>
						{presets.map((preset) => {
							const isActive = isPresetActive(preset, from, to)
							return (
								<Box
									key={`${preset.count}-${preset.unit}`}
									as="button"
									onClick={() => handlePresetClick(preset)}
									paddingX="3"
									paddingY="3"
									textAlign="left"
									borderBottomWidth="1"
									backgroundColor={isActive ? "primary" : "default"}
									style={{
										cursor: "pointer",
										transition: "all 0.15s",
									}}
								>
									<Text
										fontSize="sm"
										fontWeight={isActive ? "semibold" : "normal"}
									>
										{`Last ${preset.count} ${preset.unit}`}
									</Text>
								</Box>
							)
						})}
					</Stack>
				</Col>
			) : null}

			{/* Date Range Input */}
			<Col xs={12} sm={gettingPresets ? 10 : 12}>
				<Box
					style={{ flex: 1 }}
					paddingTop="2"
					paddingBottom="4"
					paddingLeft="2"
					paddingRight="4"
					borderLeftWidth={gettingPresets ? "1" : "0"}
					borderColor="default"
				>
					<DateRangeInput
						from={from}
						to={to}
						onChange={onChange}
						dateFormat={dateFormat}
					/>
				</Box>
			</Col>
		</Grid>
	)
}

// Get the date range for a preset
function getRangeForPreset(preset: TDateRangePreset): [Date, Date] {
	const today = new Date()
	const yesterday = subtractUnit(today, 1, "day")
	const interval: [Date, Date] = [today, today]
	interval[0] = startOf(subtractUnit(today, preset.count, preset.unit), "day")
	interval[1] = endOf(yesterday, "day")

	return interval
}

// Check if current date range matches a preset
function isPresetActive(
	preset: TDateRangePreset,
	from?: Date,
	to?: Date
): boolean {
	if (!from || !to) return false

	const [presetFrom, presetTo] = getRangeForPreset(preset)
	return isSame(presetFrom, from, "day") && isSame(presetTo, to, "day")
}
